import { Middleware } from "@reduxjs/toolkit";
import { hideLoader, showLoader } from "../reducers/loaderSlice";
import { RootState } from "../store";

/**
 * Middleware to automatically show/hide global loader based on Redux actions
 * Shows loader for actions ending with 'Request'
 * Hides loader for actions ending with 'Success' or 'Failure'
 *
 * IMPORTANT: Excludes session-related actions as they have their own SecureLoader
 * The GlobalLoader and SecureLoader should NEVER be shown at the same time
 */
/**
 * Background fetches (MED-1648): the dashboard data, the notification list and
 * the licence status load behind the page with their own inline loading states.
 * They must not cover the screen with the GlobalLoader (which blocked clicks on
 * the dashboard), and their Success/Failure must not hide a loader another
 * action is showing.
 */
const BACKGROUND_ACTION_PREFIXES = [
  "dashboard/",
  "notifications/fetchNotifications",
  "licence/fetchLicence",
];

export const isBackgroundAction = (actionType: string): boolean =>
  BACKGROUND_ACTION_PREFIXES.some((prefix) => actionType.startsWith(prefix));

export const loaderMiddleware: Middleware = (store) => (next) => (action) => {
  if (typeof action === "object" && action !== null && "type" in action) {
    const actionType = action.type as string;

    // Don't show loader for loader actions themselves
    if (actionType.startsWith("loader/")) {
      return next(action);
    }

    // Exclude ALL session-related actions (they use SecureLoader exclusively)
    const isSessionAction =
      actionType.startsWith("session/validateSession") ||
      actionType.startsWith("session/getSession") ||
      actionType.startsWith("session/requestTerminateSession") ||
      actionType === "session/validateSessionRequest" ||
      actionType === "session/validateSessionSuccess" ||
      actionType === "session/validateSessionFailure" ||
      actionType === "session/getSessionRequest" ||
      actionType === "session/getSessionSuccess" ||
      actionType === "session/getSessionFailure" ||
      actionType === "session/requestTerminateSessionRequest";

    if (isSessionAction || isBackgroundAction(actionType)) {
      return next(action);
    }

    // Check if session is currently loading (SecureLoader is active)
    const state = store.getState() as RootState;
    const isSessionLoading = state.session?.loading === true;

    // Don't show GlobalLoader if SecureLoader is active
    if (isSessionLoading) {
      return next(action);
    }
    // Show loader for Request actions
    if (actionType.endsWith("Request")) {
      store.dispatch(showLoader());
    }

    // Hide loader for Success or Failure actions
    if (actionType.endsWith("Success") || actionType.endsWith("Failure")) {
      store.dispatch(hideLoader());
    }
  }

  return next(action);
};
